/**
 * Horaires d'ouverture animés — heure de Cayenne (America/Cayenne, UTC-3).
 * Ouvert : anneau de progression de la journée + compte à rebours avant fermeture.
 * Fermé : compte à rebours avant la prochaine ouverture (aujourd'hui / demain / jour).
 */
import React, { useEffect, useState } from 'react';
import { Clock } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

// 0 = dimanche … 6 = samedi — [heure d'ouverture, heure de fermeture]
export const OPENING_HOURS = { 1: [9, 19], 2: [9, 19], 3: [9, 19], 4: [9, 19], 5: [9, 19], 6: [9, 14], 0: null };
// `label`/`hours` restent en français (référence + consommés tels quels par CadbotChat) ;
// `labelKey`/`hoursKey` servent au rendu i18n du widget via t().
export const HOURS_ROWS = [
  { days: [1, 2, 3, 4, 5], label: 'Lundi - Vendredi', labelKey: 'hours.rows.weekdays.label', hours: '9h - 19h', hoursKey: 'hours.rows.weekdays.hours' },
  { days: [6], label: 'Samedi', labelKey: 'hours.rows.saturday.label', hours: '9h - 14h', hoursKey: 'hours.rows.saturday.hours' },
  { days: [0], label: 'Dimanche', labelKey: 'hours.rows.sunday.label', hours: null, hoursKey: null },
];
const WD = { dim: 0, lun: 1, mar: 2, mer: 3, jeu: 4, ven: 5, sam: 6 };
const CIRC = 2 * Math.PI * 34;

// Interpolation {{var}} identique à LanguageContext (pour le repli hors React).
const interp = (str, vars) => (vars && typeof str === 'string'
  ? str.replace(/\{\{\s*(\w+)\s*\}\}/g, (m, k) => (vars[k] != null ? String(vars[k]) : m))
  : str);

// Repli FRANÇAIS (référence) pour les appelants HORS React — CADBOT, tests, isOpenNow — qui
// appellent computeHoursState SANS traducteur : la sortie reste identique à l'historique.
// Quand un traducteur est fourni (HoursLive via useLanguage), il prime et pilote les 7 langues.
const FR = {
  'hours.unit.hour': 'h',
  'hours.unit.minute': 'min',
  'hours.unit.second': 's',
  'hours.clock': 'il est {{time}} à Cayenne',
  'hours.ring.dayProgress': 'de la journée',
  'hours.ring.untilReopen': 'avant réouverture',
  'hours.state.openNow': 'Ouvert maintenant',
  'hours.state.closingSoon': 'Ferme bientôt',
  'hours.state.closed': 'Fermé',
  'hours.count.untilClose': 'avant la fermeture ({{hour}} h)',
  'hours.count.untilOpen': 'avant l’ouverture',
  'hours.sub.openSince': 'Ouvert depuis {{duration}}',
  'hours.sub.nextOpen': 'Prochaine ouverture : {{when}} à {{hour}} h',
  'hours.when.today': 'aujourd’hui',
  'hours.when.tomorrow': 'demain',
  'hours.mood.closingSoon': '⏳ Dernière ligne droite — on vous attend encore un peu !',
  'hours.mood.justOpened': '☕ On vient d’ouvrir — passez nous voir !',
  'hours.mood.open': '🟢 C’est ouvert, passez quand vous voulez !',
  'hours.mood.opensSoon': '🚪 On ouvre dans moins d’une heure — le rideau se lève bientôt !',
  'hours.mood.sundayRest': '🌴 Dimanche, c’est repos — on se retrouve lundi à 9 h !',
  'hours.mood.morning': '🌅 Bonjour ! On ouvre à {{hour}} h, préparez vos appareils.',
  'hours.mood.evening': '🌙 C’est fermé pour ce soir — à très vite !',
  'hours.dayName.0': 'dimanche',
  'hours.dayName.1': 'lundi',
  'hours.dayName.2': 'mardi',
  'hours.dayName.3': 'mercredi',
  'hours.dayName.4': 'jeudi',
  'hours.dayName.5': 'vendredi',
  'hours.dayName.6': 'samedi',
};
const DEFAULT_TR = (key, vars) => interp(FR[key] != null ? FR[key] : key, vars);

let fmt;
export function cayenneNow() {
  try {
    fmt = fmt || new Intl.DateTimeFormat('fr-FR', {
      timeZone: 'America/Cayenne', weekday: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
    });
    const o = {};
    fmt.formatToParts(new Date()).forEach((p) => { o[p.type] = p.value; });
    const wd = WD[(o.weekday || '').toLowerCase().replace('.', '').slice(0, 3)];
    return { d: wd === undefined ? new Date().getDay() : wd, sec: ((+o.hour) % 24) * 3600 + (+o.minute) * 60 + (+o.second) };
  } catch {
    const n = new Date();
    return { d: n.getDay(), sec: n.getHours() * 3600 + n.getMinutes() * 60 + n.getSeconds() };
  }
}

// Date du jour (YYYY-MM-DD) à Cayenne — pour la recherche d'exceptions par date. Même fuseau/util que cayenneNow.
let fmtYmd;
export function cayenneYmd(date) {
  try {
    fmtYmd = fmtYmd || new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Cayenne', year: 'numeric', month: '2-digit', day: '2-digit',
    });
    return fmtYmd.format(date || new Date()); // en-CA -> "YYYY-MM-DD"
  } catch {
    return (date || new Date()).toISOString().slice(0, 10);
  }
}

const pad = (n) => String(n).padStart(2, '0');
const split = (sec) => {
  const s = Math.max(0, Math.round(sec));
  return { h: Math.floor(s / 3600), m: Math.floor((s % 3600) / 60), s: s % 60 };
};
const short = (sec, tr) => { const p = split(sec); return (p.h ? `${p.h} ${tr('hours.unit.hour')} ` : '') + `${pad(p.m)} ${tr('hours.unit.minute')}`; };

function nextOpen(d, now) {
  for (let i = 0; i < 8; i += 1) {
    const dd = (d + i) % 7; const hh = OPENING_HOURS[dd];
    if (!hh) continue; // eslint-disable-line no-continue
    const open = hh[0] * 3600;
    if (i === 0 && now >= open) continue; // eslint-disable-line no-continue
    return { days: i, dow: dd, open: hh[0], wait: i * 86400 + open - now };
  }
  return null;
}
function sinceClose(d, now) {
  const hh = OPENING_HOURS[d];
  if (hh && now >= hh[1] * 3600) return now - hh[1] * 3600;
  for (let i = 1; i < 8; i += 1) {
    const h = OPENING_HOURS[(d - i + 7) % 7];
    if (h) return now + i * 86400 - h[1] * 3600;
  }
  return 0;
}

/**
 * État affichable pour un instant donné (exporté pour tests / CADBOT).
 * `t` est un traducteur optionnel t(key, vars) ; sans lui, repli français identique à l'historique.
 */
export function computeHoursState({ d, sec }, t) {
  const tr = typeof t === 'function' ? t : DEFAULT_TR;
  const hh = OPENING_HOURS[d];
  const open = !!hh && sec >= hh[0] * 3600 && sec < hh[1] * 3600;
  const time = `${pad(Math.floor(sec / 3600))}:${pad(Math.floor((sec % 3600) / 60))}`;
  // Séparateur ' · ' conservé littéral : CadbotChat découpe la sous-ligne sur ce motif.
  const clock = ` · ${tr('hours.clock', { time })}`;
  if (open) {
    const total = (hh[1] - hh[0]) * 3600; const since = sec - hh[0] * 3600; const left = hh[1] * 3600 - sec; const soon = left <= 1800;
    return {
      d, open: true, cls: soon ? 'soon' : '', ring: since / total, ringLabel: tr('hours.ring.dayProgress'),
      state: soon ? tr('hours.state.closingSoon') : tr('hours.state.openNow'), count: split(left), countLabel: tr('hours.count.untilClose', { hour: hh[1] }),
      sub: `${tr('hours.sub.openSince', { duration: short(since, tr) })}${clock}`,
      mood: soon ? tr('hours.mood.closingSoon')
        : (since < 3600 ? tr('hours.mood.justOpened') : tr('hours.mood.open')),
    };
  }
  const n = nextOpen(d, sec);
  if (!n) return { d, open: false, cls: 'closed', ring: 0, ringLabel: '', state: tr('hours.state.closed'), count: split(0), countLabel: '', sub: '', mood: '' };
  const sc = sinceClose(d, sec);
  const when = n.days === 0 ? tr('hours.when.today') : (n.days === 1 ? tr('hours.when.tomorrow') : tr(`hours.dayName.${n.dow}`));
  return {
    d, open: false, cls: 'closed', ring: sc / (sc + n.wait), ringLabel: tr('hours.ring.untilReopen'),
    state: tr('hours.state.closed'), count: split(n.wait), countLabel: tr('hours.count.untilOpen'),
    sub: `${tr('hours.sub.nextOpen', { when, hour: n.open })}${clock}`,
    mood: n.wait < 3600 ? tr('hours.mood.opensSoon')
      : (d === 0 ? tr('hours.mood.sundayRest')
        : (sec < 9 * 3600 ? tr('hours.mood.morning', { hour: n.open }) : tr('hours.mood.evening'))),
  };
}

export function isOpenNow() {
  return computeHoursState(cayenneNow()).open;
}

export default function HoursLive({ title }) {
  const { t } = useLanguage();
  const heading = title || t('hours.title');
  const [st, setSt] = useState(() => computeHoursState(cayenneNow(), t));

  useEffect(() => {
    let lastSec = -1;
    const tick = () => {
      const now = cayenneNow();
      if (now.sec === lastSec) return;
      lastSec = now.sec;
      setSt(computeHoursState(now, t));
    };
    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [t]);

  const pct = Math.round(Math.max(0, Math.min(1, st.ring)) * 100);
  const offset = CIRC * (1 - Math.max(0, Math.min(1, st.ring)));

  return (
    <div className={`card p5 hours ${st.cls}`} style={{ gap: 12 }} data-testid="hours-live">
      <div className="lbl"><Clock /> {heading}</div>
      <div className="live" aria-live="polite">
        <div className="ring">
          <svg viewBox="0 0 80 80" aria-hidden="true">
            <circle className="bg" cx="40" cy="40" r="34" />
            <circle className="fg" cx="40" cy="40" r="34" style={{ strokeDashoffset: offset }} />
          </svg>
          <div className="ring-in"><span>{pct}%</span><small>{st.ringLabel}</small></div>
        </div>
        <div>
          <div className="state"><i /><span>{st.state}</span></div>
          <div className="big">
            {st.count.h ? `${st.count.h} ${t('hours.unit.hour')} ` : ''}{pad(st.count.m)} {t('hours.unit.minute')} <span className="sec-digit" key={st.count.s}>{pad(st.count.s)}</span> {t('hours.unit.second')}
            <b>{st.countLabel}</b>
          </div>
          <div className="sub">{st.sub}</div>
          <div className="mood">{st.mood}</div>
        </div>
      </div>
      {HOURS_ROWS.map((r) => (
        <div key={r.label} className={`hrow ${r.days.includes(st.d) ? 'today' : ''}`}>
          <span>{t(r.labelKey)}</span>
          {r.hoursKey ? <span>{t(r.hoursKey)}</span> : <span className="closed">{t('hours.closed')}</span>}
        </div>
      ))}
    </div>
  );
}
